import { computed } from "mobx";
import { ConductorOneChildActive, Router } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import EventBus, { deadLetterTopicMessagesChanged } from "services/eventBus";
import NotificationService, { NotificationType } from "services/notificationService";
import ProcessingMethod from "entities/processingMethod";
import DeadLetterTopicsRepository from "repositories/deadLetterTopicsRepository";
import DeadLetterTopicsActionSearchListViewModel from "./actionSearch/deadLetterTopicsActionSearchListViewModel";
import DeadLetterTopicsSearchListViewModel from "./search/deadLetterTopicsSearchListViewModel";
import type DeadLetterTopicMessagesResubmissionRequest from "entities/deadLetterTopicMessagesResubmissionRequest";
import SecurityService from "services/securityService";

const navigationName = "dead-letter-topics";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [DeadLetterTopicsSearchListViewModel, DeadLetterTopicsActionSearchListViewModel],
})
export default class DeadLetterTopicsViewModel extends ConductorOneChildActive<
  DeadLetterTopicsSearchListViewModel | DeadLetterTopicsActionSearchListViewModel
> {
  private unsubscriptions: Array<() => void> = [];

  constructor(
    private deadLetterTopicsSearchListViewModel: DeadLetterTopicsSearchListViewModel,
    private deadLetterTopicsActionSearchListViewModel: DeadLetterTopicsActionSearchListViewModel,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    public repository: DeadLetterTopicsRepository,
    public notificationService: NotificationService,
    private securityService: SecurityService
  ) {
    super();
    this.navigationName = navigationName;

    this.children.push(this.deadLetterTopicsSearchListViewModel, this.deadLetterTopicsActionSearchListViewModel);
  }

  protected onInitialize(): Promise<any> | void {
    this.tryActivateChild(this.children[0]);
    this.unsubscriptions.push(this.eventBus.subscribe(deadLetterTopicMessagesChanged, this.children[0].loadData));
    return super.onInitialize();
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("system.dlt", "write");
  }

  @bound
  async onDiscardSelectedClick() {
    try {
      await this.repository.discardAll({
        discardMethod: ProcessingMethod.ProcessSelected,
        dltMessageIds: Array.from(this.deadLetterTopicsSearchListViewModel.self.selectedItems).map(item => item.id),
      });
      this.eventBus.publish(deadLetterTopicMessagesChanged());
      this.notificationService.add({ title: this.translate("dltDiscard"), type: NotificationType.SUCCESS });
      this.deadLetterTopicsSearchListViewModel.self.selectedItems.clear();
    } catch (e) {
      // General error handle is good enough
    }
  }

  @bound
  async onResubmissionSelectedClick(type: ProcessingMethod) {
    const requestObj: DeadLetterTopicMessagesResubmissionRequest =
      type === ProcessingMethod.ProcessSelected
        ? {
            resubmissionMethod: ProcessingMethod.ProcessSelected,
            dltMessageIds: Array.from(this.deadLetterTopicsSearchListViewModel.self.selectedItems).map(item => item.id),
          }
        : {
            resubmissionMethod: ProcessingMethod.ProcessAll,
          };
    try {
      await this.repository.resubmissionAll(requestObj);
      this.eventBus.publish(deadLetterTopicMessagesChanged());
      this.notificationService.add({
        title: this.translate(type === ProcessingMethod.ProcessSelected ? "dltResubmission" : "dltResubmissionAll"),
        type: NotificationType.SUCCESS,
      });
      this.deadLetterTopicsSearchListViewModel.self.selectedItems.clear();
    } catch (e) {
      // General error handle is good enough
    }
  }

  @bound
  private onDeadLetterTopicMessagesChanged(data: ReturnType<typeof deadLetterTopicMessagesChanged>) {
    this.children.forEach(children => {
      if (
        (children instanceof DeadLetterTopicsSearchListViewModel ||
          children instanceof DeadLetterTopicsActionSearchListViewModel) &&
        children.isInitialized &&
        children.items.length
      ) {
        children.loadData();
      }
    });
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.administration, `deadLetterTopics.${code}`, size);
  }
}
